import {
  Brain,
  Database,
  FileText,
  ChartBar,
  Newspaper,
  Rocket,
  Target,
  Check,
  X,
  Question,
} from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import { explainEnrichmentFailure, shouldShowEnrichmentFailure } from '@/lib/enrichmentError';

interface ProviderDisplay {
  label: string;
  icon: React.ReactNode;
  color: string;
}

const PROVIDER_DISPLAY: Record<string, ProviderDisplay> = {
  native: {
    label: 'AI',
    icon: <Brain className="h-3 w-3" />,
    color: 'text-purple-500',
  },
  apollo: {
    label: 'Apollo',
    icon: <Rocket className="h-3 w-3" />,
    color: 'text-blue-500',
  },
  hunter: {
    label: 'Hunter',
    icon: <Target className="h-3 w-3" />,
    color: 'text-orange-500',
  },
  peopledatalabs: {
    label: 'PDL',
    icon: <Database className="h-3 w-3" />,
    color: 'text-indigo-500',
  },
  explorium: {
    label: 'Explorium',
    icon: <Database className="h-3 w-3" />,
    color: 'text-cyan-500',
  },
  rocketreach: {
    label: 'RocketReach',
    icon: <Database className="h-3 w-3" />,
    color: 'text-sky-500',
  },
  cognism: {
    label: 'Cognism',
    icon: <Database className="h-3 w-3" />,
    color: 'text-violet-500',
  },
  lusha: {
    label: 'Lusha',
    icon: <Database className="h-3 w-3" />,
    color: 'text-pink-500',
  },
  uplead: {
    label: 'UpLead',
    icon: <Database className="h-3 w-3" />,
    color: 'text-teal-500',
  },
  snov: {
    label: 'Snov.io',
    icon: <Database className="h-3 w-3" />,
    color: 'text-yellow-600',
  },
  fmp: {
    label: 'FMP',
    icon: <ChartBar className="h-3 w-3" />,
    color: 'text-green-500',
  },
  alpha_vantage: {
    label: 'Alpha Vantage',
    icon: <ChartBar className="h-3 w-3" />,
    color: 'text-emerald-500',
  },
  sec_edgar: {
    label: 'SEC',
    icon: <FileText className="h-3 w-3" />,
    color: 'text-amber-600',
  },
  news: {
    label: 'News',
    icon: <Newspaper className="h-3 w-3" />,
    color: 'text-slate-500',
  },
};

// Tiny inline source pill for per-field attribution
export function SourcePill({ provider }: { provider: string }) {
  const display = PROVIDER_DISPLAY[provider];
  const label = display?.label || provider;
  // Provider name is metadata — keep it uniform muted (no per-provider rainbow).
  // The label text identifies the provider; color is not needed and was off-palette.

  return (
    <span
      title={label}
      className={cn(
        'inline-flex items-center gap-0.5 px-1 py-px rounded text-[9px] font-medium bg-muted/50 cursor-default',
        'text-muted-foreground dark:text-[#8a8f98]'
      )}
    >
      {label}
    </span>
  );
}

// Helpers to detect source-attributed values (backwards compatible)
export function isSourced(val: any): val is { value: any; sources: string[] } {
  return val && typeof val === 'object' && !Array.isArray(val)
    && 'value' in val && 'sources' in val
    && Array.isArray(val.sources)
    && (val.sources.length === 0 || typeof val.sources[0] === 'string');
}

export function getVal(val: any): any {
  return isSourced(val) ? val.value : val;
}

export function getSources(val: any): string[] | undefined {
  return isSourced(val) ? val.sources : undefined;
}

/** Recursively strip {value, sources} wrappers from an object, returning plain values. */
export function stripSources(data: any): any {
  if (data == null) return data;
  if (isSourced(data)) return data.value;
  if (Array.isArray(data)) return data.map(stripSources);
  if (typeof data === 'object') {
    const out: any = {};
    for (const [k, v] of Object.entries(data)) {
      out[k] = stripSources(v);
    }
    return out;
  }
  return data;
}

interface SourceBadgesProps {
  results: Record<string, any>;
}

export function SourceBadges({ results }: SourceBadgesProps) {
  const entries = Object.entries(results || {});
  if (entries.length === 0) return null;

  // Friendly, categorized explanation per failed provider (shown below the
  // pills and in each pill's tooltip) instead of the raw backend error string.
  const failures = entries
    .filter(([key, result]) => result?.status !== 'completed'
      && shouldShowEnrichmentFailure(key, result?.error))
    .map(([key, result]) => ({
      key,
      label: PROVIDER_DISPLAY[key]?.label || key,
      reason: explainEnrichmentFailure(key, result?.error, PROVIDER_DISPLAY[key]?.label || key),
    }));

  return (
    <div className="ml-7 mt-1.5 space-y-1.5">
      <div className="flex flex-wrap gap-1.5">
        {entries.map(([key, result]) => {
          const provider = PROVIDER_DISPLAY[key];
          const status = result?.status;
          const isCompleted = status === 'completed';

          const label = provider?.label || key;
          const icon = provider?.icon || <Question className="h-3 w-3" />;
          const color = provider?.color || 'text-muted-foreground';

          const tooltip = isCompleted
            ? `${label}: Completed`
            : `${label}: ${explainEnrichmentFailure(key, result?.error, label)}`;

          return (
            <span
              key={key}
              title={tooltip}
              className={cn(
                'inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-medium transition-opacity cursor-default',
                isCompleted
                  ? 'bg-muted/60 border border-border/40'
                  : 'bg-muted/30 border border-border/20 opacity-50'
              )}
            >
              <span className={cn(isCompleted ? color : 'text-muted-foreground')}>
                {icon}
              </span>
              <span className={cn(isCompleted ? 'text-foreground/80' : 'text-muted-foreground')}>
                {label}
              </span>
              {isCompleted ? (
                <Check className="h-2.5 w-2.5 text-green-500" />
              ) : (
                <X className="h-2.5 w-2.5 text-muted-foreground" />
              )}
            </span>
          );
        })}
      </div>

      {failures.length > 0 && (
        <ul className="space-y-1">
          {failures.map((f) => (
            <li
              key={f.key}
              className="flex items-start gap-1.5 text-[11px] leading-snug text-muted-foreground"
            >
              <X className="h-3 w-3 mt-px flex-shrink-0 text-red-400" />
              <span>
                <span className="font-medium text-foreground/80">{f.label}:</span>{' '}
                {f.reason}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
